import React, { useState, useMemo, useCallback } from 'react';
import { token } from '@atlaskit/tokens';
import VideoIcon from '@atlaskit/icon/core/migration/video--vid-camera-on';
import CrossIcon from '@atlaskit/icon/core/cross';
import { IconButton } from '@atlaskit/button/new';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { createWorkVideoEducationModalEntryPoint } from '@atlassian/jira-create-work-video-education-modal/entrypoint.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { ModalEntryPointButtonTrigger } from '@atlassian/jira-modal-entry-point-button-trigger/src/ModalEntryPointButtonTrigger.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge/src/utils/fire-analytics.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

export const CreateWorkVideoEducationModalTriggerButtonBanner = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isBannerDismissed, setIsBannerDismissed] = useState(false);

	const handleOpenModal = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'openCreateWorkVideoEducationModal',
		);
	}, [createAnalyticsEvent]);

	const handleDismissBanner = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'dismissCreateWorkVideoEducationBanner',
		);
		setIsBannerDismissed(true);
	}, [createAnalyticsEvent]);

	const entryPointProps = useMemo(() => ({}), []);

	const modalProps = useMemo(
		() => ({
			width: 'x-large',
			onOpenComplete: () => {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'open',
						actionSubject: 'modalDialog',
					}),
					'createWorkVideoEducationModal',
				);
			},
			onCloseComplete: () => {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'close',
						actionSubject: 'modalDialog',
					}),
					'createWorkVideoEducationModal',
				);
			},
		}),
		[createAnalyticsEvent],
	);

	if (isBannerDismissed) {
		return null;
	}

	return (
		<Box
			testId="issue-create-commons.ui.create-work-video-education-modal-trigger-button.create-work-video-education-banner"
			xcss={bannerContainerStyles}
		>
			<Flex alignItems="center" justifyContent="space-between" xcss={bannerItemStyles}>
				<ModalEntryPointButtonTrigger
					appearance="subtle"
					entryPoint={createWorkVideoEducationModalEntryPoint}
					entryPointProps={entryPointProps}
					interactionName="create-work-video-education-modal"
					modalProps={modalProps}
					onClick={handleOpenModal}
					useInternalModal
					testId="issue-create-commons.ui.create-work-video-education-modal-trigger-button.create-work-video-education-modal-trigger-button"
				>
					<Box xcss={openVideoModalButtonStyles}>
						<Flex alignItems="center" gap="space.100">
							<VideoIcon label={formatMessage(messages.videoIconLabel)} />
							{formatMessage(messages.openVideoModalButtonLabel)}
						</Flex>
					</Box>
				</ModalEntryPointButtonTrigger>
				<IconButton
					appearance="subtle"
					icon={CrossIcon}
					label={formatMessage(messages.closeBannerLabel)}
					onClick={handleDismissBanner}
				/>
			</Flex>
		</Box>
	);
};

const bannerContainerStyles = xcss({
	backgroundColor: 'color.background.discovery',
	paddingBlock: 'space.100',
	paddingInline: 'space.100',
	marginInline: 'space.200',
	marginBottom: 'space.100',
	borderRadius: 'border.radius.100',
});
const bannerItemStyles = xcss({
	width: '100%',
	gap: 'space.100',
});
const openVideoModalButtonStyles = xcss({
	color: 'color.link',
	fontWeight: token('font.weight.regular'),
});
